import React from 'react'

import { createTrimOnBlurHandler } from '@/utils/dom'

import { Label } from '../label'
import * as S from './text-area.styled'
import { TextAreaProps } from './text-area.types'

export const TextArea = React.forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      labelPosition = 'top',
      tooltip,
      helperText,
      error,
      characterLimit,
      name,
      id,
      className,
      size = 'medium',
      resize = 'vertical',
      value,
      disabled,
      readOnly,
      required,
      withReservedFooter,
      onChange,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const valueLength = value == null ? 0 : String(value).length
    const hasLimit = characterLimit != null
    const isOverLimit = hasLimit && valueLength > characterLimit
    // Note: exceeding the soft character limit is itself an error state.
    const hasError = !!error || isOverLimit

    const message = error ?? helperText
    const shouldShowFooter = withReservedFooter || !!message || hasLimit
    const textAreaId = id ?? name
    const helperId = message ? `${textAreaId}-message` : undefined
    const counterId = hasLimit ? `${textAreaId}-counter` : undefined
    const describedBy = [helperId, counterId].filter(Boolean).join(' ') || undefined

    const handleBlur = createTrimOnBlurHandler<HTMLTextAreaElement>((e) => {
      if (e.target.value !== String(value ?? '')) {
        onChange?.(e as React.ChangeEvent<HTMLTextAreaElement>)
      }
      onBlur?.(e)
    })

    return (
      <S.Container
        className={className}
        $labelPosition={labelPosition}
        $size={size}
        data-component-id="TextArea"
      >
        {label && (
          <Label
            htmlFor={textAreaId}
            size={size}
            disabled={disabled}
            hasError={hasError}
            tooltip={tooltip}
            required={required}
          >
            {label}
          </Label>
        )}
        <S.Field $size={size}>
          <S.TextArea
            ref={ref}
            $hasError={hasError}
            $isDisabled={!!disabled}
            $isReadOnly={!!readOnly}
            $resize={resize}
            $size={size}
            id={textAreaId}
            name={name}
            value={value}
            disabled={disabled}
            readOnly={readOnly}
            required={required}
            aria-invalid={hasError || undefined}
            aria-describedby={describedBy}
            onChange={onChange}
            onBlur={handleBlur}
            {...props}
          />
          {shouldShowFooter && (
            <S.Footer $size={size}>
              {message && (
                <S.Message id={helperId} $hasError={hasError} role={error ? 'alert' : undefined}>
                  {message}
                </S.Message>
              )}
              {hasLimit && (
                <S.Counter id={counterId} $isOverLimit={isOverLimit}>
                  <S.CurrentCount $digits={String(characterLimit).length}>
                    {valueLength}
                  </S.CurrentCount>
                  /{characterLimit}
                </S.Counter>
              )}
            </S.Footer>
          )}
        </S.Field>
      </S.Container>
    )
  },
)
